/**
 * Cost-free pre-validation for the "create route" flow.
 *
 * Everything here is pure: no AI, no Google Maps, no quota, no credits.
 * The UI (and the server) run this BEFORE spending anything so that a request
 * that is already complete goes straight to generation, and an incomplete one
 * gets a single short question instead of a generic "Erro ao processar".
 */

import { extractBasicTripRequest } from "./trip-intent";
import { extractDestinationFromPrompt } from "./destination-extraction";
import { extractExplicitLocality } from "./geo-identity";
import { expiredWindowQuestion } from "./intraday-window";
import { parseRelativeDayWindow } from "./relative-day-window";
import { detectRegionalRequest, type RegionalRequest } from "./request-routing";
import {
  parseTravelLogistics,
  logisticsClarification,
  type TravelLogistics,
} from "./arrival-window";

export type MissingField =
  | "destination"
  | "duration"
  | "time_window"
  | "logistics_arrival"
  | "logistics_departure";

export type TripFacts = {
  city?: string;
  durationHours?: number;
  /**
   * Dias pedidos. NUNCA derivado de horas: "tenho 7 horas" é sempre 1 dia.
   */
  days?: number;
  interests: string[];
  mode?: string;
  pace?: "slow" | "normal" | "intense";
  /** O pedido é do próprio dia ("hoje", "agora até às 18h"). */
  intraday?: boolean;
  /** Desvio de dia relativamente a hoje (0 = hoje, 1 = amanhã). */
  dayOffset?: number;
  /** Início do intervalo pedido, em minutos desde a meia-noite. */
  startMinutes?: number;
  /** Fim do intervalo pedido, em minutos desde a meia-noite. */
  endMinutes?: number;
  /** A hora final indicada já passou — perguntar antes de gastar. */
  windowExpired?: boolean;
  /**
   * Logística de viagem declarada no pedido (aterro, partida, bagagem,
   * hotel). Só existe o que está escrito — nunca inventado.
   */
  logistics?: TravelLogistics;
};


export type TripRequestAssessment =
  | { status: "ready"; facts: TripFacts }
  | { status: "needs_info"; missing: MissingField; question: string; facts: TripFacts }
  | { status: "regional"; regional: RegionalRequest; facts: TripFacts }
  | { status: "unsupported"; message: string; facts: TripFacts };

const fold = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

const QUESTIONS: Partial<Record<MissingField, Record<string, string>>> = {
  destination: {
    pt: "Para que cidade queres viajar?",
    en: "Which city are you travelling to?",
    es: "¿A qué ciudad quieres viajar?",
    fr: "Dans quelle ville veux-tu voyager ?",
    it: "In quale città vuoi viaggiare?",
    de: "In welche Stadt möchtest du reisen?",
  },
  duration: {
    pt: "Quantos dias vais ficar?",
    en: "How many days are you staying?",
    es: "¿Cuántos días te quedas?",
    fr: "Combien de jours restes-tu ?",
    it: "Quanti giorni resti?",
    de: "Wie viele Tage bleibst du?",
  },
  time_window: {
    pt: "Essa hora já passou. Até que horas queres explorar hoje?",
    en: "That time has already passed. Until what time do you want to explore today?",
    es: "Esa hora ya pasó. ¿Hasta qué hora quieres explorar hoy?",
    fr: "Cette heure est déjà passée. Jusqu'à quelle heure veux-tu explorer aujourd'hui ?",
    it: "Quell'ora è già passata. Fino a che ora vuoi esplorare oggi?",
    de: "Diese Uhrzeit ist schon vorbei. Bis wann möchtest du heute unterwegs sein?",
  },
};


const UNSUPPORTED: Record<string, string> = {
  pt: "Preciso de pelo menos o destino e a duração. Escreve, por exemplo: “Milão, 3 dias, cultura e gastronomia”.",
  en: "I need at least a destination and a duration. For example: “Milan, 3 days, culture and food”.",
  es: "Necesito al menos el destino y la duración. Por ejemplo: “Milán, 3 días, cultura y gastronomía”.",
  fr: "Il me faut au moins la destination et la durée. Par exemple : « Milan, 3 jours, culture et gastronomie ».",
  it: "Mi servono almeno destinazione e durata. Ad esempio: “Milano, 3 giorni, cultura e cibo”.",
  de: "Ich brauche mindestens Ziel und Dauer. Zum Beispiel: „Mailand, 3 Tage, Kultur und Essen“.",
};

export const TECHNICAL_FAILURE_MESSAGE: Record<string, string> = {
  pt: "Não conseguimos gerar a rota agora. O teu crédito não foi consumido. Tenta novamente.",
  en: "We couldn't generate the route right now. Your credit was not used. Please try again.",
  es: "No pudimos generar la ruta ahora. No se consumió tu crédito. Inténtalo de nuevo.",
  fr: "Nous n'avons pas pu générer l'itinéraire. Ton crédit n'a pas été consommé. Réessaie.",
  it: "Non siamo riusciti a generare l'itinerario. Il tuo credito non è stato consumato. Riprova.",
  de: "Wir konnten die Route gerade nicht erstellen. Dein Guthaben wurde nicht verbraucht. Bitte erneut versuchen.",
};

function pick(dict: Record<string, string> | undefined, language: string): string {
  const key = (language || "pt").slice(0, 2).toLowerCase();
  return dict?.[key] ?? dict?.en ?? dict?.pt ?? "";
}

export function technicalFailureMessage(language: string): string {
  return pick(TECHNICAL_FAILURE_MESSAGE, language);
}

const INTEREST_PATTERNS: Array<{ id: string; re: RegExp }> = [
  { id: "cultural", re: /\b(cultura\w*|culturais|museu\w*|monument\w*|historia|historic\w*|patrimonio|culture|cultural|museum\w*|kultur|arte|art)\b/ },
  { id: "food", re: /\b(gastronomia|gastronomic\w*|comida|comer|restaurante\w*|culinaria|food|cuisine|eat|dining|cibo|essen|gastronomie)\b/ },
  { id: "nature", re: /\b(natureza|natural|parque\w*|jardins?|trilh\w*|nature|parks?|hiking|natur|naturaleza)\b/ },
  { id: "viewpoint", re: /\b(miradour\w*|vistas?|panoram\w*|viewpoints?|skyline)\b/ },
  { id: "shopping", re: /\b(compras|shopping|lojas?|shops?)\b/ },
  { id: "nightlife", re: /\b(noite|noturna|bares?|bars?|nightlife|clubs?)\b/ },
  { id: "family", re: /\b(familia|criancas|kids|family|children)\b/ },
  { id: "hidden", re: /\b(escondid\w*|secret\w*|hidden|local|autentic\w*)\b/ },
];

export function detectInterests(prompt: string): string[] {
  const p = fold(prompt);
  return INTEREST_PATTERNS.filter(({ re }) => re.test(p)).map(({ id }) => id);
}

export function detectPace(prompt: string): TripFacts["pace"] {
  const p = fold(prompt);
  if (/\b(calmo|relax\w*|descanso|tranquilo|slow|chill|leisure)\b/.test(p)) return "slow";
  if (/\b(intens\w*|ver tudo|packed|maximo|aproveitar ao maximo)\b/.test(p)) return "intense";
  return undefined;
}

const DESTINATION_STOPWORDS = new Set([
  "dias", "dia", "days", "day", "horas", "hora", "hours", "hour", "ver", "visitar", "conhecer",
  "cultura", "gastronomia", "natureza", "comida", "museus", "eles", "deles", "la", "ali", "ferias",
  "viagem", "viajar", "casa", "trabalho", "noite", "manha", "tarde", "semana", "fim", "hotel",
]);

/**
 * Local destination extraction. Deliberately conservative: it only accepts a
 * candidate that looks like a proper place name, so we never send a junk value
 * to geocoding, and never invent a city.
 */
export function extractDestinationCandidate(prompt: string): string | undefined {
  const named = extractDestinationFromPrompt(prompt);
  if (named.city && !DESTINATION_STOPWORDS.has(fold(named.city))) return named.city;

  const particles = "(?:da|de|do|das|dos|del|di|du|of|the|e|and|y|et|und|a|o|os|as)";
  const patterns = [
    new RegExp(`\\b(?:vou|irei|vamos|viajo|viajamos|estarei|estou|fico|ficar)\\s+(?:a|ao|à|para|em|no|na)\\s+([\\p{Lu}][\\p{L}'’-]{1,40}(?:\\s+(?:${particles})?\\s+[\\p{Lu}][\\p{L}'’-]{1,40}){0,3})`, "u"),
    new RegExp(`(?:^|[\\s,;:])(?:em|para|a|ao|no|na|to|in|en|à|nach|verso)\\s+([\\p{Lu}][\\p{L}'’-]{1,40}(?:\\s+(?:${particles})?\\s+[\\p{Lu}][\\p{L}'’-]{1,40}){0,3})`, "u"),
    new RegExp(`^\\s*([\\p{Lu}][\\p{L}'’-]{1,40}(?:\\s+(?:${particles})?\\s+[\\p{Lu}][\\p{L}'’-]{1,40}){0,3})\\s*[,;:]`, "u"),
  ];
  for (const re of patterns) {
    const candidate = prompt.match(re)?.[1]?.trim();
    if (candidate && !DESTINATION_STOPWORDS.has(fold(candidate))) return candidate;
  }
  return undefined;
}

const WORD_NUMBERS: Record<string, number> = {
  um: 1, uma: 1, un: 1, una: 1, one: 1, ein: 1, eine: 1,
  dois: 2, duas: 2, dos: 2, due: 2, deux: 2, two: 2, zwei: 2,
  tres: 3, tre: 3, trois: 3, three: 3, drei: 3,
  quatro: 4, cuatro: 4, quattro: 4, quatre: 4, four: 4, vier: 4,
  cinco: 5, cinque: 5, cinq: 5, five: 5, funf: 5,
  seis: 6, sei: 6, six: 6, sechs: 6, sete: 7, siete: 7, sette: 7, sept: 7, seven: 7, sieben: 7,
};

/** Duration parsing for languages the deterministic PT/EN extractor misses. */
export function extractDurationFallback(prompt: string): number | undefined {
  const p = fold(prompt);
  const match = p.match(/\b(\d+(?:[.,]\d+)?|[a-z]{2,6})\s*(dias?|days?|giorni|giorno|jours?|tage|tag|noites?|nights?|horas?|hours?|ore|ora|heures?|stunden?)\b/);
  if (!match) return undefined;
  const raw = match[1];
  const value = /^\d/.test(raw) ? Number(raw.replace(",", ".")) : WORD_NUMBERS[raw];
  if (!Number.isFinite(value) || !value) return undefined;
  const isHours = /^(hora|hour|ore|ora|heure|stunde)/.test(match[2]);
  const hours = isHours ? value : value * 24;
  return hours >= 0.5 && hours <= 336 ? hours : undefined;
}

/**
 * Unidade explícita de HORAS no pedido, em qualquer das línguas suportadas.
 * Enquanto for horas, o pedido é sempre de UM dia.
 */
const EXPLICIT_HOURS_UNIT =
  /\b\d{1,2}(?:[.,]\d)?\s*(?:h|horas?|hours?|hrs?|heures?|ore|ora|stunden?)\b/u;

/** Dias explícitos ("3 dias", "3 days", "3 giorni"…). */
const EXPLICIT_DAYS_UNIT =
  /\b(\d{1,2})\s*(?:dias?|days?|giorni|giorno|jours?|tage|tag|noites?|nights?|noches?|notti)\b/u;

export function extractTripFacts(prompt: string, now: Date = new Date(), timeZone?: string): TripFacts {
  const basic = extractBasicTripRequest(prompt);
  // Uma janela do próprio dia ("até às 18 horas") define a duração e nunca
  // pode ser lida como "18 horas" nem convertida em dias.
  const window = parseRelativeDayWindow(prompt, timeZone, now);
  const durationHours = window
    ? window.durationHours
    : basic.durationHours ?? extractDurationFallback(prompt);

  const folded = fold(prompt ?? "");
  const explicitDays = Number(folded.match(EXPLICIT_DAYS_UNIT)?.[1] ?? "");
  // Ordem vinculativa: dias explícitos > horas explícitas/janela > derivação.
  const days = Number.isFinite(explicitDays) && explicitDays > 0
    ? explicitDays
    : window || EXPLICIT_HOURS_UNIT.test(folded)
      ? 1
      : durationHours
        ? Math.max(1, Math.round(durationHours / 24))
        : undefined;

  return {
    // "Estou em X" nomeia a localidade e dispensa confirmação de cidade; só
    // depois se recorre ao candidato genérico extraído do texto.
    city: extractExplicitLocality(prompt) ?? extractDestinationCandidate(prompt),

    durationHours,
    days,
    interests: detectInterests(prompt),
    mode: basic.mode,
    pace: detectPace(prompt),
    intraday: window ? true : undefined,
    dayOffset: window?.dayOffset,
    startMinutes: window?.startMinutes,
    endMinutes: window?.endMinutes,
    windowExpired: window?.expired || undefined,
    logistics: parseTravelLogistics(prompt),
  };
}

/**
 * Decides, without spending anything, whether we already have the minimum data
 * to generate a route.
 */
export function assessTripRequest(
  prompt: string,
  language = "pt",
  now: Date = new Date(),
  timeZone?: string,
): TripRequestAssessment {
  const text = (prompt ?? "").replace(/\s+/gu, " ").trim();
  const facts = extractTripFacts(text, now, timeZone);

  if (!text) {
    return { status: "unsupported", message: pick(UNSUPPORTED, language), facts };
  }

  // Estradas nomeadas, países, regiões e viagens intercidades NUNCA são um
  // pedido de cidade: seguem para o planeador Pro com o contrato completo,
  // antes de qualquer pergunta, IA, Maps ou crédito.
  const regional = detectRegionalRequest(text, language);
  if (regional) return { status: "regional", regional, facts };

  if (!facts.city) {
    // Nothing at all usable → explain instead of asking a blind question.
    if (!facts.durationHours && facts.interests.length === 0 && text.length < 4) {
      return { status: "unsupported", message: pick(UNSUPPORTED, language), facts };
    }
    return { status: "needs_info", missing: "destination", question: pick(QUESTIONS.destination, language), facts };
  }
  if (facts.windowExpired) {
    return { status: "needs_info", missing: "time_window", question: expiredWindowQuestion(language), facts };
  }
  if (!facts.durationHours) {
    // Pedido do próprio dia sem hora final: é utilizável na mesma, com uma
    // duração curta por defeito — nunca perguntar "quantos dias?".
    if (facts.intraday) return { status: "ready", facts: { ...facts, durationHours: 3, days: 1 } };
    return { status: "needs_info", missing: "duration", question: pick(QUESTIONS.duration, language), facts };
  }
  // Logística de chegada/partida incompleta (aterro sem hora, aterro sem
  // hotel nem bagagem, partida sem hora): desambiguação obrigatória antes de
  // gastar. "Criar sem logística" nunca pergunta.
  const logisticsQuestion = logisticsClarification(facts.logistics ?? {}, language);
  if (logisticsQuestion) {
    return {
      status: "needs_info",
      missing: logisticsQuestion.field,
      question: logisticsQuestion.question,
      facts,
    };
  }
  return { status: "ready", facts };
}


/**
 * Merges a clarification answer into the original prompt so the user never has
 * to repeat what they already said.
 */
export function mergeClarificationAnswer(basePrompt: string, missing: MissingField, answer: string): string {
  const base = (basePrompt ?? "").replace(/\s+/gu, " ").trim();
  const reply = (answer ?? "").replace(/\s+/gu, " ").trim();
  if (!reply) return base;
  if (missing === "time_window") {
    // Remove a janela antiga (dia relativo + horas) para evitar duplicados.
    const cleaned = base
      .replace(/\b(?:hoje|amanh[ãa]|depois\s+de\s+amanh[ãa])\s+(?:[àa]s?\s+)?\d{1,2}(?:[:h.]\d{2})?\s*(?:h|horas?)?(?:\s*(?:a|à|at[eé]|-)\s*(?:[àa]s?\s+)?\d{1,2}(?:[:h.]\d{2})?\s*(?:h|horas?)?)?/iu, " ")
      .replace(/\b(?:hoje|amanh[ãa]|depois\s+de\s+amanh[ãa])\b/iu, " ")
      .replace(/\b(?:at[eé]|às?)\s+(?:[àa]s?\s+)?\d{1,2}(?:[:h.]\d{2})?\s*(?:h|horas?)?(?:\s*(?:a|à|at[eé]|-)\s*(?:[àa]s?\s+)?\d{1,2}(?:[:h.]\d{2})?\s*(?:h|horas?)?)?/iu, " ")
      .trim();
    // Resposta simples só com a hora final → formata como "até às X".
    const simpleHour = reply.match(/^\d{1,2}(?::\d{2})?$/);
    if (simpleHour) return `${cleaned} até às ${simpleHour[0]}`.replace(/\s{2,}/g, " ").trim();
    return `${cleaned} ${reply}`.replace(/\s{2,}/g, " ").trim();
  }
  if (missing === "logistics_arrival" || missing === "logistics_departure") {
    // Resposta só com a hora ("14", "14:30") → formatada como frase natural
    // para o parser de logística; caso contrário (hotel, bagagem, opção
    // "Criar sem logística") a resposta entra tal e qual no pedido.
    const simpleHour = reply.match(/^(\d{1,2})(?::(\d{2}))?$/);
    if (simpleHour) {
      const timeText = `${simpleHour[1]}:${simpleHour[2] ?? "00"}`;
      return missing === "logistics_arrival"
        ? `${base} chego às ${timeText}`.replace(/\s{2,}/g, " ").trim()
        : `${base} partida às ${timeText}`.replace(/\s{2,}/g, " ").trim();
    }
    return `${base} ${reply}`.replace(/\s{2,}/g, " ").trim();
  }
  if (missing === "duration" && /^\d+(?:[.,]\d+)?$/.test(reply)) {

    return `${base} ${Number(reply.replace(",", ".")) >= 24 ? `${reply} horas` : `${reply} dias`}`.trim();
  }
  if (missing === "destination") {
    // A mesma resposta pode chegar novamente por Enter/clique ou porque uma
    // versão antiga do extrator não reconheceu "quero ir Atenas". Nunca
    // acrescentar outra ocorrência da cidade ao pedido.
    const foldedBase = fold(base);
    const foldedReply = fold(reply).replace(/^[\s,;:]*(?:em|para|a|to|in)\s+/u, "").trim();
    if (foldedReply && new RegExp(`(?:^|[^\\p{L}\\p{N}])${foldedReply.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}(?=$|[^\\p{L}\\p{N}])`, "u").test(foldedBase)) {
      return base;
    }
    if (!/\b(?:em|para|a|to|in)\b/i.test(reply) && reply.split(" ").length <= 3) {
      return `${base} em ${reply}`.trim();
    }
  }
  return `${base} ${reply}`.trim();
}

export type ParseAttemptOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; retryable: boolean; kind: "timeout" | "rate_limited" | "server_error" | "invalid_response" | "unknown" };

export type PromptFlowResult<T> =
  | { kind: "question"; missing: MissingField; question: string; basePrompt: string; facts: TripFacts }
  | { kind: "regional"; regional: RegionalRequest; basePrompt: string; facts: TripFacts }
  | { kind: "unsupported"; message: string }
  | { kind: "ready"; value: T; attempts: number }
  | { kind: "technical"; message: string; attempts: number };

/**
 * Single entry point used by the UI: pre-validate for free, then (only when the
 * data is sufficient) run the paid step once, with one idempotent retry for
 * transient failures. Never runs the paid step while clarifying.
 */
export async function runTripPromptFlow<T>(options: {
  prompt: string;
  language?: string;
  timeZone?: string;
  run: (prompt: string) => Promise<ParseAttemptOutcome<T>>;
  maxAttempts?: number;
}): Promise<PromptFlowResult<T>> {
  const language = options.language ?? "pt";
  const assessment = assessTripRequest(options.prompt, language, new Date(), options.timeZone);
  if (assessment.status === "unsupported") return { kind: "unsupported", message: assessment.message };
  if (assessment.status === "regional") {
    return {
      kind: "regional",
      regional: assessment.regional,
      basePrompt: (options.prompt ?? "").replace(/\s+/gu, " ").trim(),
      facts: assessment.facts,
    };
  }
  if (assessment.status === "needs_info") {
    return {
      kind: "question",
      missing: assessment.missing,
      question: assessment.question,
      basePrompt: (options.prompt ?? "").replace(/\s+/gu, " ").trim(),
      facts: assessment.facts,
    };
  }

  const maxAttempts = Math.max(1, Math.min(options.maxAttempts ?? 2, 3));
  let attempts = 0;
  let lastRetryable = false;
  while (attempts < maxAttempts) {
    attempts += 1;
    const outcome = await options.run(options.prompt);
    if (outcome.ok) return { kind: "ready", value: outcome.value, attempts };
    lastRetryable = outcome.retryable;
    if (!outcome.retryable) break;
  }
  void lastRetryable;
  return { kind: "technical", message: technicalFailureMessage(language), attempts };
}
